import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { AuthData } from '../../provider/AuthProvider';
import { Logo } from '../../components/images';
import { FaEye, FaEyeSlash, FaSpinner, FaExclamationTriangle, FaEnvelope } from 'react-icons/fa';

const SignIn = () => {
    const { login, user, loading: authLoading, sendVerificationEmail } = AuthData();
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showVerificationPrompt, setShowVerificationPrompt] = useState(false);
    const [verificationEmailSent, setVerificationEmailSent] = useState(false);
    const [generalError, setGeneralError] = useState(''); // Local error state
    const errorRef = useRef(''); // Ref to persist error across rerenders
    const [forceUpdate, setForceUpdate] = useState(0); // Force component update

    // Check for persisted error on component mount/rerender
    useEffect(() => {
        const persistedError = sessionStorage.getItem('loginError');
        if (persistedError && !generalError && !errorRef.current) {
            setGeneralError(persistedError);
            errorRef.current = persistedError;
            // Clear it after setting to avoid showing stale errors
            sessionStorage.removeItem('loginError');
        }
    }, [generalError]);

    // Redirect if already authenticated
    useEffect(() => {
        if (user?.isAuthenticated && !authLoading) {
            console.log('✅ User already authenticated, redirecting to dashboard');
            if (!(user.displayName || user.FirstName || user.LastName) && !user.Interests) {
                navigate('/',{state:{formsSignup:true}});
            } else {
                navigate('/explore');
            }
        }
    }, [user, authLoading, navigate]);

    // Don't render the form if we're loading or user is already authenticated
    if (authLoading || user?.isAuthenticated) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-200 to-orange-500">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
                    <p className="text-white">Loading...</p>
                </div>
            </div>
        );
    }

    const validationSchema = Yup.object({
        email: Yup.string()
            .email('Invalid email address')
            .required('Email is required'),
        password: Yup.string()
            .min(6, 'Password must be at least 6 characters')
            .required('Password is required'),
    });

    const handleSubmit = async (values, { setSubmitting, setErrors, setStatus }) => {
        // Prevent any default form submission behavior
        console.log('🔑 Starting login attempt...');
        
        try {
            setIsSubmitting(true);
            setStatus(null);
            setErrors({});
            setShowVerificationPrompt(false);
            // Clear any previous errors
            setGeneralError('');
            errorRef.current = '';
            sessionStorage.removeItem('loginError'); // Clear persisted error
            
            console.log('🔑 Attempting student login...');
            
            const user = await login(values.email, values.password);
            
            if (user) {
                console.log('✅ Login successful');
                
                // Handle remember me
                if (values.rememberMe) {
                    localStorage.setItem('studentEmail', values.email);
                } else {
                    localStorage.removeItem('studentEmail');
                }
                
                // Check email verification status
                if (!user.emailVerified) {
                    console.log('⚠️ Email not verified, showing prompt');
                    setShowVerificationPrompt(true);
                    return; // Don't navigate yet
                }
                
                // Navigate to dashboard
                if (!(user.displayName || user.FirstName || user.LastName) && !user.Interests) {
                    navigate('/',{state:{formsSignup:true}});
                } else {
                    navigate('/explore');
                }
            }
            
        } catch (error) {
            console.error('❌ Login failed:', error);
            console.log('🔍 Error details:', {
                message: error.message,
                code: error.code,
                stack: error.stack
            });
            
            let errorMessage = '';
            
            // Handle specific error types (similar to employer signin)
            if (error.code === 'auth/invalid-credential' || error.message.includes('invalid-credential')) {
                errorMessage = 'Invalid email or password. Please check your credentials and try again.';
            } else if (error.message.includes('user-not-found')) {
                errorMessage = 'No account found with this email address.';
            } else if (error.message.includes('wrong-password')) {
                errorMessage = 'Incorrect password. Please try again.';
            } else if (error.message.includes('invalid-email')) {
                errorMessage = 'Invalid email address format.';
            } else if (error.message.includes('too-many-requests')) {
                errorMessage = 'Too many failed attempts. Please try again later.';
            } else if (error.message.includes('email-not-verified')) {
                errorMessage = 'Please verify your email address before signing in.';
            } else {
                errorMessage = error.message || 'Login failed. Please try again.';
            }
            
            console.log('📝 Setting error message:', errorMessage);
            
            // Set error in state, ref, AND sessionStorage for maximum persistence
            setGeneralError(errorMessage);
            errorRef.current = errorMessage;
            sessionStorage.setItem('loginError', errorMessage); // Persist across rerenders
            setForceUpdate(prev => prev + 1); // Force component to show error
            
            console.log('✅ Error state set, should display now');
            
        } finally {
            console.log('🏁 Login attempt finished');
            setIsSubmitting(false);
            setSubmitting(false);
        }
    };

    const handleSendVerificationEmail = async () => {
        try {
            await sendVerificationEmail();
            setVerificationEmailSent(true);
        } catch (error) {
            console.error('Error sending verification email:', error);
        }
    };

    const handleContinueUnverified = () => {
        // Continue to dashboard even without verification
        if (!(user?.displayName || user?.FirstName || user?.LastName) && !user?.Interests) {
            navigate('/',{state:{formsSignup:true}});
        } else {
            navigate('/explore');
        }
    };

    // Get remembered email
    const getInitialValues = () => {
        const rememberedEmail = localStorage.getItem('studentEmail');
        return {
            email: rememberedEmail || '',
            password: '',
            rememberMe: !!rememberedEmail
        };
    };

    // If login was successful but email is not verified, show verification prompt
    if (showVerificationPrompt) {
        return (
            <div className='bg-gradient-to-br from-orange-200 to-orange-500 min-h-screen w-full p-4 sm:p-6 lg:p-10'>
                {/* Header */}
                <div>
                    <Link to="/main-signup">
                        <div className='flex flex-row items-center mb-6 sm:mb-8'>
                            <img src={Logo} className='w-8 h-8 sm:w-10 sm:h-10' alt="Logo"/>
                            <p className='text-lg sm:text-xl lg:text-2xl ml-2 font-medium text-gray-800'>Oodles Internships</p>
                        </div>
                    </Link>
                </div>

                <div className="flex justify-center items-center">
                    <div className='flex flex-col gap-6 p-8 md:p-12 bg-white shadow-2xl rounded-xl max-w-md w-full'>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FaExclamationTriangle className="text-yellow-500 text-2xl" />
                            </div>
                            <h2 className='text-2xl font-bold text-gray-800 mb-2'>Email Verification Required</h2>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <p className="text-yellow-800 mb-3">
                                Your email address is not verified. Please verify your email to access all features.
                            </p>
                            <p className="text-yellow-700 text-sm">
                                Email: <strong>{user?.userd?.email}</strong>
                            </p>
                        </div>

                        {verificationEmailSent && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="flex items-center text-green-800">
                                    <FaEnvelope className="mr-2" />
                                    <span className="text-sm">Verification email sent! Check your inbox and spam folder.</span>
                                </div>
                            </div>
                        )}

                        <div className="space-y-3">
                            <button
                                onClick={handleSendVerificationEmail}
                                className="w-full bg-yellow-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-yellow-600 transition-colors flex items-center justify-center"
                            >
                                <FaEnvelope className="mr-2" />
                                {verificationEmailSent ? 'Resend Verification Email' : 'Send Verification Email'}
                            </button>

                            <button
                                onClick={handleContinueUnverified}
                                className="w-full bg-gray-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-600 transition-colors"
                            >
                                Continue Without Verification
                            </button>
                        </div>

                        <p className="text-gray-600 text-sm text-center">
                            Some features may be limited until you verify your email address.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='bg-gradient-to-br from-orange-200 to-orange-500 min-h-screen w-full p-4 sm:p-6 lg:p-10'>
            {/* Header */}
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8'>
                <Link to="/main-signup">
                    <div className='flex flex-row items-center mb-4 sm:mb-0'>
                        <img src={Logo} className='w-8 h-8 sm:w-10 sm:h-10' alt="Logo"/>
                        <p className='text-lg sm:text-xl lg:text-2xl ml-2 font-medium text-gray-800'>Oodles Internships</p>
                    </div>
                </Link>
                
                <div className='flex sm:flex-row justify-center gap-2 sm:gap-3'>               
                    <Link to='/about'                             
                    className='text-md sm:text-md hover:underline'               
                    >                 
                        About               
                    </Link>               
                    <Link to='/faq'                              
                    className='text-md sm:text-md hover:underline'               
                    >                 
                        FAQs               
                    </Link>             
                </div>
            </div>

            {/* Main Content */}
            <div className="flex justify-center items-center">    
                {/* Login Card */}
                <div className='flex flex-col gap-6 p-8 md:p-12 bg-white shadow-2xl rounded-xl max-w-md w-full'>
                    {/* Header */}
                    <div className='text-center'>
                        <h2 className='text-2xl font-bold text-orange-400 mb-2'>Intern Sign In</h2>
                        <p className='text-gray-600'>Welcome back! Please sign in to your account.</p>
                    </div>

                    {/* General Error Message - OUTSIDE OF FORM with ref backup */}
                    {(generalError || errorRef.current) && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                            {generalError || errorRef.current}
                        </div>
                    )}

                    {/* Login Form */}
                    <Formik
                        initialValues={getInitialValues()}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ errors, isSubmitting }) => (
                            <Form className='space-y-6'>
                                {/* Email Field */}
                                <div>
                                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                                        Email Address
                                    </label>
                                    <Field
                                        name="email"
                                        type="email"
                                        placeholder="Enter your email"
                                        className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                                    />
                                    <ErrorMessage name="email" component="div" className="text-red-600 text-sm mt-1" />
                                </div>

                                {/* Password Field */}
                                <div>
                                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                                        Password
                                    </label>
                                    <div className='relative'>
                                        <Field
                                            name="password"
                                            type={showPassword ? "text" : "password"}
                                            placeholder="Enter your password"
                                            className='w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent'
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors'
                                        >
                                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                    </div>
                                    <ErrorMessage name="password" component="div" className="text-red-600 text-sm mt-1" />
                                </div>

                                {/* Remember Me & Forgot Password */}
                                <div className='flex items-center justify-between text-sm'>
                                    <label className='flex items-center'>
                                        <Field
                                            type="checkbox"
                                            name="rememberMe"
                                            className='w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500'
                                        />
                                        <span className='ml-2 text-gray-700'>Remember me</span>
                                    </label>
                                    <Link 
                                        to="/forgot-password" 
                                        className='text-orange-500 hover:text-orange-600 font-medium'
                                    >
                                        Forgot password?
                                    </Link>
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className='w-full bg-orange-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-orange-600 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center'
                                >
                                    {isSubmitting ? (
                                        <>
                                            <FaSpinner className="animate-spin mr-2" />
                                            Signing In...
                                        </>
                                    ) : (
                                        'Sign In'
                                    )}
                                </button>

                                {/* Sign Up Link */}
                                <div className='text-center pt-4 border-t border-gray-200'>
                                    <p className='text-gray-600'>
                                        Don't have an account?{' '}
                                        <Link to="/register" className='text-orange-500 font-medium hover:text-orange-600'>
                                            Create Account
                                        </Link>
                                    </p>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </div>
    );
};

export default SignIn;